/**
 * SavoirCareBoardPage.jsx — SAVOIR CARE Emergency Department patient flow board.
 *
 * What: A Kanban-style board that tracks ED patients across clinical stages
 *       (triage → … → complete) plus a visually distinct, terminal
 *       Patient-Elected Redirect column. Cards advance via an "Advance" button
 *       (primary path) or drag-and-drop (enhancement).
 * Why:  Gives ED staff a single live view of every patient's position in the
 *       flow, sourced from CareStart check-in (front-desk / bedside), VisitStart
 *       completion (`createVisitStartSavoirCareBoardEntry`, governance toggle
 *       `savoirCareOnComplete`, default off), and a manual-add form (walk-ins).
 *
 * ─── ADR (ported from HealthDesk Experimental, adapted for MinusOne) ──────────
 *  - Route: /dashboard/savoircare (facility dashboard route).
 *  - Auth: facility dashboard (role "facility") + global admin. canWrite gates
 *    all board mutations; Firestore rules enforce facility membership.
 *  - Movement: "Advance" button is primary; @dnd-kit drag-and-drop is enhancement.
 *  - Data source: front-desk check-in (checkInPatientFromCareStart), bedside
 *    (createSavoirCareEntryFromCareStart), and VisitStart completion
 *    (createVisitStartSavoirCareBoardEntry when `savoirCareOnComplete` is true).
 *    Callables read the msi3CmrAccessTokens CareStart token for CareStart paths;
 *    do NOT read careStartSessions at runtime. VisitStart writes independently
 *    from visitStartSessions.
 *  - Storage: savoirCareBoards/{facilityId}/patients/{entryId}. Stage field
 *    `savoir_care_stage` (snake_case). ESI clinician-assigned (`savoir_care_esi`),
 *    careLevel kept separately as immutable CareStart source.
 *  - Facility scope: resolved from the user's location membership (never from URL);
 *    enforced again in Firestore rules.
 *  - Patient-Elected Redirect: terminal, gray, documented patient choice — NOT a
 *    clinical disposition and never implying clinical clearance.
 *  - ChartMind deep-link: opens MinusOne ChartMind recording (/dashboard/chartmind/
 *    recording). ChartMind is a professional-dashboard surface, so this link is
 *    role-dependent — a facility-only operator may not have access.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Tightest coupling: useSavoirCareBoard, SavoirCareBoardColumn, PatientElectedRedirectModal.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AddRounded, QrCodeScannerRounded, HourglassEmptyRounded } from '@mui/icons-material';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';
import ConfirmDialog from 'components/common/ConfirmDialog';
import { subscribeFacilityInterpreterRequests } from 'services/interpreterRequests/interpreterRequestService';
import useSavoirCareBoard from '../hooks/useSavoirCareBoard';
import {
  SAVOIR_CARE_STAGES,
  SAVOIR_CARE_STAGE_LABELS,
  CLINICAL_STAGE_ORDER,
} from '../constants/savoirCareStages';
import SavoirCareBoardColumn from './SavoirCareBoardColumn';
import PatientElectedRedirectModal from './PatientElectedRedirectModal';
import EmergencyInterventionDialog from './EmergencyInterventionDialog';
import SavoirCareCheckinPanel from './SavoirCareCheckinPanel';
import { getLoungeMetaChip } from '../utils/savoirCareTheme';

/** Live clock tick for time-since-arrival labels (single shared timer). */
// Shared clock for time-since-arrival. 30s is coarse enough for the board.
// Tests must fake this timer — a real setInterval is the open-handle that
// made Jest `--forceExit` (cleanup unmounts, but a live interval still
// keeps the process if unmount is skipped or delayed).
const CLOCK_TICK_MS = 30_000;

/** ESI options offered in the manual-add form (1 most acute … 5 least acute). */
const ESI_OPTIONS = [1, 2, 3, 4, 5];

/** MinusOne ChartMind recording entry (professional dashboard surface). */
const CHARTMIND_RECORDING_PATH = '/dashboard/chartmind/recording';

const SavoirCareBoardPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    entries,
    loading,
    error,
    retryLoad,
    facilityId,
    canWrite,
    userRole,
    advanceStage,
    moveToStage,
    dismissExpectedEntry,
    setFlag,
    setEscalated,
    flagEmergencyIntervention,
    clearEmergencyIntervention,
    recordPatientElectedRedirect,
    createManualEntry,
    setPhysicalLocation,
  } = useSavoirCareBoard();

  // Single shared clock so every card computes time-since-arrival from one timer.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Redirect documentation modal state.
  const [redirectEntry, setRedirectEntry] = useState(null);

  // Emergency-intervention dialog state. `fromEsiOne` adapts the copy when the
  // dialog is opened by the ESI 1 assignment prompt.
  const [emergencyDialog, setEmergencyDialog] = useState(null);

  // Front-desk CareStart check-in drawer (facility operator).
  const [checkinOpen, setCheckinOpen] = useState(false);
  const canCheckIn = userRole === 'facility';
  const [interpreterRequestCount, setInterpreterRequestCount] = useState(0);

  useEffect(() => {
    if (!facilityId) {
      setInterpreterRequestCount(0);
      return undefined;
    }
    return subscribeFacilityInterpreterRequests(
      facilityId,
      (rows) => setInterpreterRequestCount(rows.length),
    );
  }, [facilityId]);

  // Manual-add dialog state (walk-in fallback).
  const [addOpen, setAddOpen] = useState(false);
  const [addComplaint, setAddComplaint] = useState('');
  const [addAge, setAddAge] = useState('');
  const [addEsi, setAddEsi] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);
  const [actionError, setActionError] = useState(null);
  // Transient info — e.g. opening ChartMind with no CareStart data.
  const [infoMessage, setInfoMessage] = useState(null);

  // ConfirmDialog targets for destructive one-click actions.
  const [dismissTarget, setDismissTarget] = useState(null);
  const [clearInterventionTarget, setClearInterventionTarget] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [clearingIntervention, setClearingIntervention] = useState(false);

  const loungeMetaChip = getLoungeMetaChip(theme);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  // Group entries by stage once per snapshot (single pass).
  const entriesByStage = useMemo(() => {
    const grouped = {};
    for (const entry of entries) {
      const stage = entry.savoir_care_stage || SAVOIR_CARE_STAGES.TRIAGE;
      (grouped[stage] = grouped[stage] || []).push(entry);
    }
    return grouped;
  }, [entries]);

  const incomingEntries = entriesByStage[SAVOIR_CARE_STAGES.INCOMING] || [];
  const expectedEntries = entriesByStage[SAVOIR_CARE_STAGES.EXPECTED] || [];
  const redirectEntries = entriesByStage[SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT] || [];

  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event;
      if (!over) return;
      const entry = entries.find((e) => e.entryId === active.id);
      if (!entry) return;

      // `over.id` is either a column (stage) id or another card id; resolve to a stage.
      const overEntry = entries.find((e) => e.entryId === over.id);
      const targetStage = overEntry ? overEntry.savoir_care_stage : over.id;
      if (!targetStage || targetStage === entry.savoir_care_stage) return;

      try {
        await moveToStage(entry, targetStage);
      } catch (err) {
        console.error('[SavoirCare] Drag move failed:', err);
        setActionError(err?.message || 'Could not move patient.');
      }
    },
    [entries, moveToStage]
  );

  const handleAdvance = useCallback(
    async (entry) => {
      try {
        await advanceStage(entry);
      } catch (err) {
        console.error('[SavoirCare] Advance failed:', err);
        setActionError(err?.message || 'Could not advance patient.');
      }
    },
    [advanceStage]
  );

  const handleFlag = useCallback(
    async (entry, color) => {
      try {
        await setFlag(entry, color);
      } catch (err) {
        console.error('[SavoirCare] Flag failed:', err);
        setActionError(err?.message || 'Could not update flag.');
      }
    },
    [setFlag]
  );

  const handleEscalate = useCallback(
    async (entry, escalated) => {
      try {
        await setEscalated(entry, escalated);
      } catch (err) {
        console.error('[SavoirCare] Escalation failed:', err);
        setActionError(err?.message || 'Could not update escalation.');
      }
    },
    [setEscalated]
  );

  const handleConfirmRedirect = useCallback(
    async (doc) => {
      if (!redirectEntry) return;
      await recordPatientElectedRedirect(redirectEntry, doc);
      setRedirectEntry(null);
    },
    [redirectEntry, recordPatientElectedRedirect]
  );

  // Open the intervention-name dialog (manual "Flag emergency intervention" action).
  const handleFlagEmergencyIntervention = useCallback((entry) => {
    setEmergencyDialog({ entry, fromEsiOne: false });
  }, []);

  // Open this patient in ChartMind. Existing encounter → open it by draft id.
  // Otherwise pass board context so ChartMind can write chartmindEncounterId back.
  const handleOpenChartMind = useCallback(
    (entry) => {
      if (!entry?.entryId) return;
      if (entry.chartmindEncounterId) {
        navigate(`${CHARTMIND_RECORDING_PATH}?draft=${encodeURIComponent(entry.chartmindEncounterId)}`);
        return;
      }
      const params = new URLSearchParams({
        savoirCareEntryId: entry.entryId,
        savoirCareFacilityId: facilityId || '',
      });
      navigate(`${CHARTMIND_RECORDING_PATH}?${params.toString()}`);
      // Manual-add patients with no CareStart packet open a blank encounter.
      if (!entry.careStartTokenId) {
        setInfoMessage('No CareStart data — encounter will be blank.');
      }
    },
    [navigate, facilityId]
  );

  const handleSetLocation = useCallback(
    async (entry, value) => {
      try {
        await setPhysicalLocation(entry, value);
      } catch (err) {
        console.error('[SavoirCare] Set location failed:', err);
        setActionError(err?.message || 'Could not update location.');
      }
    },
    [setPhysicalLocation]
  );

  const handleClearEmergencyIntervention = useCallback(
    async (entry) => {
      try {
        await clearEmergencyIntervention(entry);
      } catch (err) {
        console.error('[SavoirCare] Clear intervention failed:', err);
        setActionError(err?.message || 'Could not clear the intervention flag.');
      }
    },
    [clearEmergencyIntervention]
  );

  const handleRequestDismissExpected = useCallback((entry) => {
    setDismissTarget(entry);
  }, []);

  const handleRequestClearEmergencyIntervention = useCallback((entry) => {
    setClearInterventionTarget(entry);
  }, []);

  const handleConfirmDismissExpected = useCallback(async () => {
    if (!dismissTarget) return;
    setDismissing(true);
    try {
      await dismissExpectedEntry(dismissTarget);
      setDismissTarget(null);
    } catch (err) {
      console.error('[SavoirCare] Dismiss expected failed:', err);
      setActionError(err?.message || 'Could not dismiss expected patient.');
    } finally {
      setDismissing(false);
    }
  }, [dismissTarget, dismissExpectedEntry]);

  const handleConfirmClearIntervention = useCallback(async () => {
    if (!clearInterventionTarget) return;
    setClearingIntervention(true);
    try {
      await handleClearEmergencyIntervention(clearInterventionTarget);
      setClearInterventionTarget(null);
    } finally {
      setClearingIntervention(false);
    }
  }, [clearInterventionTarget, handleClearEmergencyIntervention]);

  const handleConfirmEmergency = useCallback(
    async (interventionName) => {
      if (!emergencyDialog?.entry) return;
      await flagEmergencyIntervention(emergencyDialog.entry, interventionName);
      setEmergencyDialog(null);
    },
    [emergencyDialog, flagEmergencyIntervention]
  );

  const handleManualAdd = useCallback(async () => {
    setAddBusy(true);
    setAddError(null);
    try {
      const ageNum = addAge.trim() === '' ? null : Number(addAge);
      const esiValue = addEsi === '' ? null : Number(addEsi);
      const newEntryId = await createManualEntry({
        chiefComplaintSummary: addComplaint.trim(),
        patientAge: Number.isFinite(ageNum) ? ageNum : null,
        savoir_care_esi: esiValue,
      });
      setAddComplaint('');
      setAddAge('');
      setAddEsi('');
      setAddOpen(false);
      // ESI 1 prompt: never auto-set the emergency flag silently — offer to name
      // the intervention for a newly added ESI 1 patient.
      if (esiValue === 1 && newEntryId) {
        setEmergencyDialog({
          entry: { entryId: newEntryId, emergency_intervention: false, chiefComplaintSummary: addComplaint.trim() },
          fromEsiOne: true,
        });
      }
    } catch (err) {
      console.error('[SavoirCare] Manual add failed:', err);
      setAddError(err?.message || 'Could not add patient.');
    } finally {
      setAddBusy(false);
    }
  }, [addAge, addComplaint, addEsi, createManualEntry]);

  if (!facilityId) {
    return (
      <Box sx={{ mt: { xs: 1, sm: 5 } }}>
        <Alert severity="warning">
          {error || 'Facility not found. Your account must be associated with a facility to use SAVOIR CARE.'}
        </Alert>
      </Box>
    );
  }

  // "Active" = patients in a clinical stage that is not yet complete. Expected /
  // Incoming (pre-arrival), complete, and redirect (terminal) are excluded.
  const activeCount = entries.filter(
    (e) => e.savoir_care_stage !== SAVOIR_CARE_STAGES.EXPECTED &&
           e.savoir_care_stage !== SAVOIR_CARE_STAGES.INCOMING &&
           e.savoir_care_stage !== SAVOIR_CARE_STAGES.COMPLETE &&
           e.savoir_care_stage !== SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT
  ).length;

  // Results-Pending Lounge summary: count entries across all columns whose
  // physical_location contains "lounge" (case-insensitive). Display only.
  const loungeCount = entries.filter(
    (e) => (e.physical_location || '').toLowerCase().includes('lounge')
  ).length;

  return (
    <Box sx={{ mb: 4 }} data-testid="savoir-care-board">
      <DashboardPageHeader
        title="SAVOIR CARE"
        subtitle={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" component="span">
              {activeCount} active patient{activeCount !== 1 ? 's' : ''} in the ED flow
            </Typography>
            {loungeCount > 0 && loungeMetaChip && (
              <Chip
                icon={<HourglassEmptyRounded sx={{ fontSize: '0.85rem' }} />}
                label={`${loungeCount} in Results Lounge`}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 600,
                  bgcolor: loungeMetaChip.bg,
                  color: loungeMetaChip.color,
                  border: '1px solid',
                  borderColor: loungeMetaChip.border,
                  '& .MuiChip-icon': { color: loungeMetaChip.color },
                }}
              />
            )}
            {interpreterRequestCount > 0 ? (
              <Chip
                component={RouterLink}
                to="/dashboard/interpreter-requests"
                clickable
                size="small"
                color="warning"
                label={`🗣 ${interpreterRequestCount} interpreter request${
                  interpreterRequestCount !== 1 ? 's' : ''
                }`}
                sx={{ height: 22, fontWeight: 600 }}
                data-testid="savoir-care-interpreter-badge"
              />
            ) : null}
          </Box>
        }
        actions={
          <>
            {canCheckIn && (
              <Button
                variant="outlined"
                startIcon={<QrCodeScannerRounded />}
                onClick={() => setCheckinOpen(true)}
                data-testid="savoir-care-check-in"
              >
                Check in
              </Button>
            )}
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() => setAddOpen(true)}
                data-testid="savoir-care-add-patient"
              >
                Add patient
              </Button>
            )}
          </>
        }
      />

      {error && (
        <DataLoadErrorAlert message={error} onRetry={retryLoad} />
      )}
      {infoMessage && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfoMessage(null)}>
          {infoMessage}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : !error ? (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', overflowX: 'auto', pb: 2 }}>
            {/* CareVerify Expected (left-most). Both urgent + elective bookings land here. */}
            <SavoirCareBoardColumn
              stage={SAVOIR_CARE_STAGES.EXPECTED}
              label={SAVOIR_CARE_STAGE_LABELS[SAVOIR_CARE_STAGES.EXPECTED]}
              entries={expectedEntries}
              canWrite={canWrite}
              isExpectedColumn
              now={now}
              onAdvance={handleAdvance}
              onDismissExpected={handleRequestDismissExpected}
              onFlag={handleFlag}
              onEscalate={handleEscalate}
              onRedirect={setRedirectEntry}
              onFlagEmergencyIntervention={handleFlagEmergencyIntervention}
              onClearEmergencyIntervention={handleRequestClearEmergencyIntervention}
              onOpenChartMind={handleOpenChartMind}
              onSetLocation={handleSetLocation}
            />

            {/* Pre-arrival, non-clinical Incoming column (purple). Populated by
                front-desk CareStart check-in. */}
            <SavoirCareBoardColumn
              stage={SAVOIR_CARE_STAGES.INCOMING}
              label={SAVOIR_CARE_STAGE_LABELS[SAVOIR_CARE_STAGES.INCOMING]}
              entries={incomingEntries}
              canWrite={canWrite}
              isIncomingColumn
              now={now}
              onAdvance={handleAdvance}
              onFlag={handleFlag}
              onEscalate={handleEscalate}
              onRedirect={setRedirectEntry}
              onFlagEmergencyIntervention={handleFlagEmergencyIntervention}
              onClearEmergencyIntervention={handleRequestClearEmergencyIntervention}
              onOpenChartMind={handleOpenChartMind}
              onSetLocation={handleSetLocation}
            />

            {/* Visual separator between pre-arrival and the clinical workflow. */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'divider', mx: 1 }} />
            </Box>

            {CLINICAL_STAGE_ORDER.map((stage) => (
              <SavoirCareBoardColumn
                key={stage}
                stage={stage}
                label={SAVOIR_CARE_STAGE_LABELS[stage]}
                entries={entriesByStage[stage] || []}
                canWrite={canWrite}
                now={now}
                onAdvance={handleAdvance}
                onFlag={handleFlag}
                onEscalate={handleEscalate}
                onRedirect={setRedirectEntry}
                onFlagEmergencyIntervention={handleFlagEmergencyIntervention}
                onClearEmergencyIntervention={handleRequestClearEmergencyIntervention}
                onOpenChartMind={handleOpenChartMind}
                onSetLocation={handleSetLocation}
              />
            ))}

            {/* Visual separator before the non-clinical, terminal redirect column. */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'divider', mx: 1 }} />
            </Box>

            <SavoirCareBoardColumn
              stage={SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT}
              label={SAVOIR_CARE_STAGE_LABELS[SAVOIR_CARE_STAGES.PATIENT_ELECTED_REDIRECT]}
              entries={redirectEntries}
              canWrite={canWrite}
              isRedirectColumn
              now={now}
              onAdvance={handleAdvance}
              onFlag={handleFlag}
              onEscalate={handleEscalate}
              onRedirect={setRedirectEntry}
              onFlagEmergencyIntervention={handleFlagEmergencyIntervention}
              onClearEmergencyIntervention={handleRequestClearEmergencyIntervention}
              onOpenChartMind={handleOpenChartMind}
              onSetLocation={handleSetLocation}
            />
          </Box>
        </DndContext>
      ) : null}

      <ConfirmDialog
        open={Boolean(dismissTarget)}
        onClose={() => {
          if (!dismissing) setDismissTarget(null);
        }}
        title="Dismiss expected patient?"
        message={
          dismissTarget
            ? `“${dismissTarget.chiefComplaintSummary || 'Patient'}” will be removed from the Expected column. This elective booking will no longer appear on the live board.`
            : ''
        }
        confirmLabel="Dismiss"
        confirmColor="error"
        onConfirm={handleConfirmDismissExpected}
        loading={dismissing}
      />

      <ConfirmDialog
        open={Boolean(clearInterventionTarget)}
        onClose={() => {
          if (!clearingIntervention) setClearInterventionTarget(null);
        }}
        title="Clear emergency intervention flag?"
        message={
          clearInterventionTarget
            ? `Remove the emergency intervention flag from “${clearInterventionTarget.chiefComplaintSummary || 'Patient'}”? The card will no longer appear as an emergency.`
            : ''
        }
        confirmLabel="Clear flag"
        confirmColor="error"
        onConfirm={handleConfirmClearIntervention}
        loading={clearingIntervention}
      />

      {/* Patient-elected redirect documentation modal */}
      <PatientElectedRedirectModal
        open={Boolean(redirectEntry)}
        entry={redirectEntry}
        onCancel={() => setRedirectEntry(null)}
        onConfirm={handleConfirmRedirect}
      />

      {/* Emergency intervention naming (manual flag + ESI 1 prompt) */}
      <EmergencyInterventionDialog
        open={Boolean(emergencyDialog)}
        entry={emergencyDialog?.entry || null}
        fromEsiOne={Boolean(emergencyDialog?.fromEsiOne)}
        onCancel={() => setEmergencyDialog(null)}
        onConfirm={handleConfirmEmergency}
      />

      {/* Front-desk CareStart check-in (facility operator) */}
      {canCheckIn && (
        <SavoirCareCheckinPanel
          open={checkinOpen}
          facilityId={facilityId}
          onClose={() => setCheckinOpen(false)}
        />
      )}

      {/* Manual-add patient (walk-in fallback) */}
      <Dialog
        open={addOpen}
        onClose={addBusy ? undefined : () => setAddOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="savoir-care-add-dialog"
      >
        <DialogTitle>Add patient</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Manually add a walk-in patient to the board. CareStart-sourced patients arrive automatically via check-in.
          </Typography>
          <TextField
            label="Chief complaint"
            value={addComplaint}
            onChange={(e) => setAddComplaint(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 80, 'data-testid': 'savoir-care-add-complaint' }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Age"
            type="number"
            value={addAge}
            onChange={(e) => setAddAge(e.target.value)}
            fullWidth
            inputProps={{ 'data-testid': 'savoir-care-add-age' }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="ESI (optional)"
            select
            value={addEsi}
            onChange={(e) => setAddEsi(e.target.value)}
            fullWidth
            SelectProps={{ 'data-testid': 'savoir-care-add-esi' }}
          >
            <MenuItem value="">Not assigned</MenuItem>
            {ESI_OPTIONS.map((level) => (
              <MenuItem key={level} value={level}>
                ESI {level}
              </MenuItem>
            ))}
          </TextField>
          {addError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {addError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addBusy}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleManualAdd}
            loading={addBusy}
            disabled={!addComplaint.trim()}
            data-testid="savoir-care-add-submit"
          >
            Add to board
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SavoirCareBoardPage;
